<script setup>
/**
 * OCHAT: los datos del hotel.
 *
 * LA ZONA HORARIA NO ES UN DETALLE DE PRESENTACIÓN
 * Una noche es una fecha EN EL HOTEL, no en el servidor ni en el navegador de quien
 * atiende. Todo el cálculo de disponibilidad cuelga de este campo, así que se explica
 * en pantalla en lugar de esconderlo entre opciones.
 *
 * La lista de zonas es corta a propósito: son las que un hotel de la región va a
 * necesitar. Los valores son identificadores IANA, que es lo que el modelo valida
 * contra ActiveSupport::TimeZone. Una lista completa serían cientos de opciones para
 * un campo que se toca una vez en la vida.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  property: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const { t } = useI18n();
const uiFlags = useMapGetter('bookingSettings/getUIFlags');

const TIMEZONES = [
  'America/Argentina/Buenos_Aires',
  'America/Montevideo',
  'America/Santiago',
  'America/Asuncion',
  'America/Sao_Paulo',
  'America/La_Paz',
  'America/Lima',
  'America/Bogota',
  'America/Mexico_City',
  'UTC',
];

const CURRENCIES = ['ARS', 'USD', 'UYU', 'CLP', 'PYG', 'BRL', 'EUR'];

const blank = () => ({
  name: '',
  timezone: 'America/Argentina/Buenos_Aires',
  currency: 'ARS',
  checkin_time: '14:00',
  checkout_time: '10:00',
});

const form = ref(blank());

watch(
  () => props.property,
  property => {
    form.value = property
      ? {
          name: property.name,
          timezone: property.timezone,
          currency: property.currency,
          checkin_time: property.checkin_time,
          checkout_time: property.checkout_time,
        }
      : blank();
  },
  { immediate: true }
);

const isEditing = computed(() => Boolean(props.property));
const canSubmit = computed(() => form.value.name.trim().length > 0);

const save = async () => {
  try {
    if (isEditing.value) {
      await store.dispatch('bookingSettings/updateProperty', {
        id: props.property.id,
        ...form.value,
      });
      useAlert(t('BOOKINGS.SETTINGS.PROPERTY.UPDATED'));
    } else {
      await store.dispatch('bookingSettings/createProperty', form.value);
      useAlert(t('BOOKINGS.SETTINGS.PROPERTY.CREATED'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('BOOKINGS.SETTINGS.PROPERTY.FAILED')
    );
  }
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="save">
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.NAME') }}
        <input
          v-model="form.name"
          type="text"
          required
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.CURRENCY') }}
        <select
          v-model="form.currency"
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        >
          <option v-for="code in CURRENCIES" :key="code" :value="code">
            {{ code }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11 sm:col-span-2">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.TIMEZONE') }}
        <select
          v-model="form.timezone"
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        >
          <option v-for="zone in TIMEZONES" :key="zone" :value="zone">
            {{ zone }}
          </option>
        </select>
        <span class="text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.PROPERTY.TIMEZONE_HINT') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.CHECKIN_TIME') }}
        <input
          v-model="form.checkin_time"
          type="time"
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.CHECKOUT_TIME') }}
        <input
          v-model="form.checkout_time"
          type="time"
          class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        />
      </label>
    </div>

    <div>
      <button
        type="submit"
        :disabled="!canSubmit || uiFlags.isCreating || uiFlags.isUpdating"
        class="px-4 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
      >
        {{
          isEditing
            ? $t('BOOKINGS.SETTINGS.PROPERTY.SAVE')
            : $t('BOOKINGS.SETTINGS.PROPERTY.CREATE')
        }}
      </button>
    </div>
  </form>
</template>
